// src/App.js
import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { apiFetch } from "./apiFetch";
import Register from "./components/Register";
import Login from "./components/Login";
import CompleteRegistration from "./components/CompleteRegistration";
import LandingPage from "./components/LandingPage";
import AccountDisabled from "./components/AccountDisabled";
import MaintenancePage from "./components/MaintenancePage";



// Player & Admin dashboards (these stay separate)
import PlayerDashboard from "./components/Dashboard/PlayerDashboard/PlayerDashboard";
import AdminDashboard from "./components/Dashboard/AdminDashboard/AdminDashboard";

// Role dashboards (agent / scout / club all reuse RoleDashboard under RoleRoute)
import {
  AgentRoleDashboard,
  ScoutRoleDashboard,
  ClubRoleDashboard,
} from "./components/Dashboard/RoleDashboard";

// ✅ Unified Role Verification (agent / scout / club share one component)
import RoleVerification from "./components/Dashboard/RoleDashboard/RoleExtras/RoleVerification.jsx";
import SubscriptionPage from "./components/Dashboard/RoleDashboard/RoleExtras/SubscriptionPage.jsx";
import RoleProfile from "./components/Dashboard/RoleDashboard/RoleExtras/RoleProfile";

// ✅ Role-aware View Applications (shared by agent/scout/club)
import ViewApplications from "./components/Dashboard/RoleDashboard/RoleExtras/ViewApplications.jsx";

// ✅ Shared, role-aware VSN transfer pages (Contract Offer / Manage / Show Interest)
import ContractOffer from "./components/Dashboard/RoleDashboard/RoleExtras/ContractOffer.jsx";
import ManageContract from "./components/Dashboard/RoleDashboard/RoleExtras/ManageContract.jsx";
import ShowInterest from "./components/Dashboard/RoleDashboard/RoleExtras/ShowInterest.jsx";

// ✅ Leaderboard (shared by scout / agent / club)
import Leaderboard from "./components/Dashboard/RoleDashboard/RoleExtras/Leaderboard.jsx";
// ✅ Player Leaderboard
import PlayerLeaderboard from "./components/Dashboard/PlayerDashboard/PlayerExtras/Leaderboard";

//Feed
import Feed from "./components/Dashboard/RoleDashboard/RoleExtras/Feed";

// Player profile (separate world)
import PlayerProfile from "./components/Dashboard/PlayerDashboard/PlayerExtras/PlayerProfile";

// Applications
import ApplyForm from "./components/Dashboard/PlayerDashboard/PlayerExtras/ApplyForm";
import ApplyVSNIndexForm from "./components/Dashboard/PlayerDashboard/PlayerExtras/ApplyVSNIndexForm";


// Agent Extras (legacy standalone routes)
// ❌ Bookmarks import removed – now handled via role dashboard
// ❌ OffertoSignPlayer import removed – now handled via role dashboard
// ❌ AgentNotifications import removed – Notifications now handled via role dashboard
// ⬇️ old AgentContractOffer / AgentShowInterest / AgentManageContract removed
import Inbox from "./components/Dashboard/AgentDashboard/Inbox";

// New Agent Extras
import AgentReport from "./components/Dashboard/AgentDashboard/AgentExtras/AgentReport";
import AgentVSNWorld from "./components/Dashboard/AgentDashboard/AgentExtras/AgentVSNWorld";

// Player Extras
import PlayerNotifications from "./components/Dashboard/PlayerDashboard/PlayerExtras/PlayerNotifications";
import SigningOffer from "./components/Dashboard/PlayerDashboard/PlayerExtras/signingoffer";
import SigningInterest from "./components/Dashboard/PlayerDashboard/PlayerExtras/signinginterest";
import Contracts from "./components/Dashboard/PlayerDashboard/PlayerExtras/contracts";
import TrainingVideos from "./components/Dashboard/PlayerDashboard/PlayerExtras/TrainingVideos";
import MyJourney from "./components/Dashboard/PlayerDashboard/PlayerExtras/MyJourney";
import PlayerReporting from "./components/Dashboard/PlayerDashboard/PlayerExtras/PlayerReporting";

// New Player Inbox Tabs
import PlayerChats from "./components/Dashboard/PlayerDashboard/PlayerExtras/Chats";
import PlayerComms from "./components/Dashboard/PlayerDashboard/PlayerExtras/Comms";
import PlayerExtras from "./components/Dashboard/PlayerDashboard/PlayerExtras/Extras";

// Admin Extras
// ⬇️ AgentContractReview removed – now using unified AdminContractReview

import RefreshYoutubeVideos from "./components/Dashboard/AdminDashboard/AllUsers/RefreshYoutubeVideos";
// ✅ New unified admin role verification screen
import ManageUserStatus from "./components/Dashboard/AdminDashboard/AllUsers/ManageUserStatus";
import AdminRoleVerification from "./components/Dashboard/AdminDashboard/AllUsers/AdminRoleVerification";
// ✅ New unified admin contract review screen
import AdminContractReview from "./components/Dashboard/AdminDashboard/AllUsers/AdminContractReview";
// ✅ New contract termination screen
import AdminContractTermination from "./components/Dashboard/AdminDashboard/AllUsers/AdminContractTermination";
// ✅ New admin final player contract report screen
import AdminFinalPlayerReport from "./components/Dashboard/AdminDashboard/AllUsers/AdminFinalPlayerReport";
import AdminVSNIndexReview from "./components/Dashboard/AdminDashboard/AllUsers/AdminVSNIndexReview";
import AdminPlayerCareerReview from "./components/Dashboard/AdminDashboard/AllUsers/AdminPlayerCareerReview";
import AdminRoleReportReview from "./components/Dashboard/AdminDashboard/AllUsers/AdminRoleReportReview";
import AdminUnderageMessagingReview from "./components/Dashboard/AdminDashboard/AllUsers/AdminUnderageMessagingReview";
import ApproveBetaAccess from "./components/Dashboard/AdminDashboard/AllUsers/ApproveBetaAccess";
import MessageUsers from "./components/Dashboard/AdminDashboard/AllUsers/MessageUsers";
import PlayerVideoProfile from "./components/Dashboard/PlayerDashboard/PlayerVideoProfile";
import RoleChats from "./components/Dashboard/RoleDashboard/RoleExtras/RoleChats";
import AdminMaintenancePage from "./components/Dashboard/AdminDashboard/AllUsers/AdminMaintenancePage";
import LimitDiscoverability from "./components/Dashboard/AdminDashboard/AllUsers/LimitDiscoverability";
import UserLookup from "./components/Dashboard/AdminDashboard/AllUsers/UserLookup";
// ✅ Admin Leaderboard
import AdminLeaderboard from "./components/Dashboard/AdminDashboard/AllUsers/Leaderboard";
import ContractMonitor from "./components/Dashboard/AdminDashboard/AllUsers/ContractMonitor";
import RevenueSharing from "./components/Dashboard/AdminDashboard/AllUsers/RevenueSharing";
import VideoReview from "./components/Dashboard/AdminDashboard/AllUsers/VideoReview";


import { auth } from "./firebase-config";
import { onAuthStateChanged } from "firebase/auth";

/**
 * RequireProfile:
 * - Blocks access to agent/scout/club dashboards if the user hasn't finished registration.
 * - Players and admins get redirected to their own dashboards instead.
 */
function RequireProfile({ children }) {
  const role = (
    localStorage.getItem("userRole") ||
    localStorage.getItem("role") ||
    ""
  ).toLowerCase();

  // Player/Admin shouldn't land in role dashboards
  if (role === "player") return <Navigate to="/player-dashboard" replace />;
  if (role === "admin") return <Navigate to="/admin-dashboard" replace />;

  // agent/scout/club must have their specific ID in localStorage
  const idKeyMap = { agent: "agentId", scout: "scoutId", club: "clubId" };
  const idKey = idKeyMap[role];
  const hasId = idKey ? !!localStorage.getItem(idKey) : false;

  if (!role || !idKey || !hasId) {
    return <Navigate to="/complete-registration" replace />;
  }

  return children;
}






function RedirectToRoleChats() {
  const role =
    (localStorage.getItem("userRole") ||
      localStorage.getItem("role") ||
      "").toLowerCase();

  if (role === "scout") {
    return <Navigate to="/scout-dashboard/chats" replace />;
  }

  if (role === "club") {
    return <Navigate to="/club-dashboard/chats" replace />;
  }

  return <Navigate to="/agent-dashboard/chats" replace />;
}



//Redirect after Payment
function RedirectAfterPayment() {
  const role =
    (localStorage.getItem("userRole") ||
      localStorage.getItem("role") ||
      "").toLowerCase();

  if (role === "scout") {
    return <Navigate to="/scout-dashboard" replace />;
  }

  if (role === "club") {
    return <Navigate to="/club-dashboard" replace />;
  }

  return <Navigate to="/agent-dashboard" replace />;
}




/**
 * RedirectToRolePath:
 * - Used for back-compat if something hits /dashboard/*
 * - Sends to the correct kebab-case dashboard path.
 */
function RedirectToRolePath() {
  const role = (
    localStorage.getItem("userRole") ||
    localStorage.getItem("role") ||
    ""
  ).toLowerCase();

  if (role === "scout") return <Navigate to="/scout-dashboard" replace />;
  if (role === "club") return <Navigate to="/club-dashboard" replace />;
  if (role === "player") return <Navigate to="/player-dashboard" replace />;
  if (role === "admin") return <Navigate to="/admin-dashboard" replace />;
  return <Navigate to="/agent-dashboard" replace />;
}

/**
 * RedirectToViewPosts:
 * - Back-compat for old /view-posts route.
 * - Sends to the correct role dashboard view-posts page.
 */
function RedirectToViewPosts() {
  const role = (
    localStorage.getItem("userRole") ||
    localStorage.getItem("role") ||
    ""
  ).toLowerCase();

  if (role === "scout") {
    return <Navigate to="/scout-dashboard/view-posts" replace />;
  }
  if (role === "club") {
    return <Navigate to="/club-dashboard/view-posts" replace />;
  }
  // default to agent
  return <Navigate to="/agent-dashboard/view-posts" replace />;
}

/**
 * RedirectToViewApplications:
 * - Back-compat for old /view-applications route.
 * - Sends to the correct role dashboard view-applications page.
 */
function RedirectToViewApplications() {
  const role = (
    localStorage.getItem("userRole") ||
    localStorage.getItem("role") ||
    ""
  ).toLowerCase();

  if (role === "scout") {
    return <Navigate to="/scout-dashboard/view-applications" replace />;
  }
  if (role === "club") {
    return <Navigate to="/club-dashboard/view-applications" replace />;
  }
  // default to agent
  return <Navigate to="/agent-dashboard/view-applications" replace />;
}

/**
 * RedirectToBookmarks:
 * - Back-compat for old /bookmarks route.
 * - Sends to the correct role dashboard bookmarks page.
 */
function RedirectToBookmarks() {
  const role = (
    localStorage.getItem("userRole") ||
    localStorage.getItem("role") ||
    ""
  ).toLowerCase();

  if (role === "scout") {
    return <Navigate to="/scout-dashboard/bookmarks" replace />;
  }
  if (role === "club") {
    return <Navigate to="/club-dashboard/bookmarks" replace />;
  }
  // default to agent
  return <Navigate to="/agent-dashboard/bookmarks" replace />;
}

/**
 * RedirectToOfferToSign:
 * - Back-compat for old /offer-to-sign/:playerId route.
 * - Sends to the correct role dashboard offer-to-sign page.
 */
function RedirectToOfferToSign() {
  const role = (
    localStorage.getItem("userRole") ||
    localStorage.getItem("role") ||
    ""
  ).toLowerCase();

  const { playerId } = useParams();

  let base;
  if (role === "scout") base = "/scout-dashboard";
  else if (role === "club") base = "/club-dashboard";
  else base = "/agent-dashboard"; // default to agent

  return <Navigate to={`${base}/offer-to-sign/${playerId}`} replace />;
}

/**
 * RedirectToNotifications:
 * - Back-compat for old /agent-notifications route.
 * - Sends to the correct role dashboard notifications page.
 */
function RedirectToNotifications() {
  const role = (
    localStorage.getItem("userRole") ||
    localStorage.getItem("role") ||
    ""
  ).toLowerCase();

  if (role === "scout") {
    return <Navigate to="/scout-dashboard/notifications" replace />;
  }
  if (role === "club") {
    return <Navigate to="/club-dashboard/notifications" replace />;
  }
  // default to agent
  return <Navigate to="/agent-dashboard/notifications" replace />;
}



function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    if (!window.gtag) return;

    // Track all page views
    window.gtag("event", "page_view", {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location]);

  return null;
}




function App() {
  // user === undefined -> still checking auth
  // user === null      -> signed out
  // user === object    -> signed in
  const [user, setUser] = useState(undefined);


  // ✅ Maintenance settings from backend
  const [maintenance, setMaintenance] = useState({
    loading: true,
    enabled: false,
    message: "",
    schedule: "",
  });

  useEffect(() => {
    const loadMaintenance = async () => {
      try {
        const res = await apiFetch("/maintenance", { method: "GET" });
        if (!res.ok) throw new Error("Failed to load maintenance settings");

        const data = await res.json();

        setMaintenance({
          loading: false,
          enabled: Boolean(data.maintenanceMode),
          message: data.maintenanceMessage || "",
          schedule: data.maintenanceSchedule || "",
        });


      } catch (err) {
        console.error("Failed to load maintenance:", err);
        // ✅ fail-open: don't lock users out if the check fails
        setMaintenance({ loading: false, enabled: false, message: "", schedule: "" });

      }
    };

    loadMaintenance();
  }, []);




  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser || null);
    });
    return () => unsubscribe();
  }, []);



  

  // ✅ Refresh disabled status when app loads / page refreshes
  useEffect(() => {
    const DEV = process.env.NODE_ENV !== "production";

    const refreshDisabledStatus = async () => {
      try {
        if (!user) return;

        // -------------------------
        // STEP 1 — Call /me first
        // (forces AllUsers auto-upsert/create)
        // -------------------------
        const meRes = await apiFetch("/me", { method: "GET" });


        if (!meRes.ok) {
          if (DEV) console.warn("/me failed:", meRes.status);
          return;
        }

        const meData = await meRes.json();

        const uid = (user?.uid || "").trim();
        if (!uid) return;

        // keep UID cached
        localStorage.setItem("authUid", uid);

        if (DEV) console.log("🔎 Disabled status check UID:", uid);

        // -------------------------
        // STEP 2 — Now status check
        // -------------------------
        const res = await apiFetch(
          `/api/all-users/status/${encodeURIComponent(uid)}`,
          { method: "GET" }
        );

        if (!res.ok) {
          const errText = await res.text().catch(() => "");

          // onboarding case: user exists in Firebase but not yet in app system
          if (
            res.status === 403 &&
            errText.includes("User not registered in system")
          ) {
            localStorage.setItem("isDisabled", "false");

            if (DEV) {
              console.log(
                "⏭️ Skipping disabled status check until registration is complete"
              );
            }

            return;
          }

          if (DEV) {
            console.warn("Disabled status check failed:", res.status, errText);
          }

          // safe fallback
          if (res.status === 404) {
            localStorage.setItem("isDisabled", "false");
          }

          return;
        }

        const data = await res.json();

        if (DEV) console.log("✅ Disabled status response:", data);

        localStorage.setItem(
          "isDisabled",
          String(Boolean(data.isDisabled))
        );

      } catch (err) {
        if (DEV) {
          console.error("❌ Error refreshing disabled status:", err);
        }
      }
    };

    if (user !== undefined) {
      refreshDisabledStatus();
    }
  }, [user]);






  
  // Helper to protect routes without flashing unauth state
  const gate = (el) => {
    const role = (
      localStorage.getItem("userRole") ||
      localStorage.getItem("role") ||
      ""
    ).toLowerCase();

    // still loading Firebase auth
    if (user === undefined) return <div />;

    // ✅ Admin can always pass (localStorage-based admin)
    if (role === "admin") {
      return el;
    }

    // ✅ Must be logged in
    if (!user) {
      return <Navigate to="/login" replace />;
    }

    // ✅ Block disabled accounts
    const disabled = localStorage.getItem("isDisabled");
    if (disabled === "true") {
      return <Navigate to="/account-disabled" replace />;
    }

    return el;
  };


  const isPrivateBeta = process.env.REACT_APP_PRIVATE_BETA === "true";
  const isSignupDisabled = process.env.REACT_APP_SIGNUP_DISABLED === "true";

  

  const role = (
    localStorage.getItem("userRole") ||
    localStorage.getItem("role") ||
    ""
  ).toLowerCase();


  // ✅ block everyone except admins
  const location = useLocation();
  const isAdminArea =
    location.pathname.startsWith("/admin-dashboard") ||
    location.pathname.startsWith("/admin/");

  if (maintenance.loading) return null;

  // ✅ Only block when maintenance is ON (no loading screen)
  if (maintenance.enabled && role !== "admin" && !isAdminArea) {
    return (
      <MaintenancePage
        message={maintenance.message}
        schedule={maintenance.schedule}
      />
    );
  }

  
  return (
    <div className="App">
      <AnalyticsTracker />
      {isPrivateBeta && (
        <div
          style={{
            background: "black",
            color: "yellow",
            textAlign: "center",
            padding: "10px",
          }}
        >
          ⚠️ Private Testing Mode Enabled – Unauthorized access prohibited
        </div>
      )}

      <Routes>
        {/* Landing / root redirect */}
        <Route path="/" element={<LandingPage />} />

        <Route
          path="/payment-success"
          element={gate(<RedirectAfterPayment />)}
        />




        {/* Public / auth routes */}
        <Route
          path="/register"
          element={
            isSignupDisabled ? (
              <div>Signup is disabled during private testing.</div>
            ) : (
              <Register />
            )
          }
        />
        <Route path="/login" element={<Login />} />

        <Route path="/account-disabled" element={<AccountDisabled />} />
        <Route path="/maintenance" element={<MaintenancePage />} />





        <Route
          path="/complete-registration"
          element={gate(<CompleteRegistration />)}
        />

        {/* =========================
        SUBSCRIPTION (Agent / Scout / Club)
        ========================= */}
        <Route
          path="/agent-dashboard/subscription"
          element={gate(
            <RequireProfile>
              <SubscriptionPage />
            </RequireProfile>
          )}
        />

        <Route
          path="/scout-dashboard/subscription"
          element={gate(
            <RequireProfile>
              <SubscriptionPage />
            </RequireProfile>
          )}
        />

        <Route
          path="/club-dashboard/subscription"
          element={gate(
            <RequireProfile>
              <SubscriptionPage />
            </RequireProfile>
          )}
        />


        

        <Route
          path="/agent-profile/:profileSlug"
          element={gate(<RoleProfile roleOverride="agent" />)}
        />
        <Route
          path="/scout-profile/:profileSlug"
          element={gate(<RoleProfile roleOverride="scout" />)}
        />
        <Route
          path="/club-profile/:profileSlug"
          element={gate(<RoleProfile roleOverride="club" />)}
        />


        





        {/* =========================
           KEBAB-CASE DASHBOARD ROUTES
           ========================= */}
        <Route
          path="/agent-dashboard/*"
          element={gate(
            <RequireProfile>
              <AgentRoleDashboard />
            </RequireProfile>
          )}
        />
        <Route
          path="/scout-dashboard/*"
          element={gate(
            <RequireProfile>
              <ScoutRoleDashboard />
            </RequireProfile>
          )}
        />
        <Route
          path="/club-dashboard/*"
          element={gate(
            <RequireProfile>
              <ClubRoleDashboard />
            </RequireProfile>
          )}
        />
        

        {/* Back-compat: legacy PascalCase -> redirect to kebab-case */}
        <Route
          path="/AgentDashboard/*"
          element={<Navigate to="/agent-dashboard" replace />}
        />
        <Route
          path="/ScoutDashboard/*"
          element={<Navigate to="/scout-dashboard" replace />}
        />
        <Route
          path="/ClubDashboard/*"
          element={<Navigate to="/club-dashboard" replace />}
        />

        {/* =========================
            LEADERBOARD (Shared by Agent, Scout, Club)
        ========================= */}
        <Route
          path="/leaderboard"
          element={gate(
            <RequireProfile>
              <Leaderboard />
            </RequireProfile>
          )}
        />

        {/* =========================
       PLAYER LEADERBOARD
        ========================= */}
        <Route
          path="/player-leaderboard"
          element={gate(<PlayerLeaderboard />)}
        />



        {/* Back-compat: generic /dashboard */}
        <Route path="/dashboard/*" element={<RedirectToRolePath />} />

        {/* Player & Admin dashboards stay separate */}
        <Route path="/player-dashboard/*" element={gate(<PlayerDashboard />)} />
        <Route path="/admin-dashboard" element={gate(<AdminDashboard />)} />



        {/* Player profile route */}
        <Route
          path="/player-profile/:profileSlug"
          element={gate(<PlayerProfile />)}
        />

       
        {/* =========================
           ROLE VERIFICATION ROUTES (Unified - user side)
           ========================= */}
        <Route
          path="/agent-verification"
          element={gate(<RoleVerification role="agent" />)}
        />
        <Route
          path="/scout-verification"
          element={gate(<RoleVerification role="scout" />)}
        />
        <Route
          path="/club-verification"
          element={gate(<RoleVerification role="club" />)}
        />

        {/* =========================
           APPLICATIONS
           ========================= */}
        <Route path="/apply/:opportunityId" element={gate(<ApplyForm />)} />
        <Route
          path="/apply-transfermarkt/:userId"
          element={gate(<ApplyVSNIndexForm />)}
        />


        {/* 🔹 NEW: per-opportunity applications view (ID last) */}
        <Route
          path="/opportunity/applications/:id"
          element={gate(
            <RequireProfile>
              <ViewApplications />
            </RequireProfile>
          )}
        />

        {/* =========================
           AGENT EXTRAS (legacy standalone pages)
           ========================= */}

        {/* 🔁 /view-posts now redirects into the role-aware dashboard page */}
        <Route path="/view-posts" element={<RedirectToViewPosts />} />

        {/* 🔁 /view-applications now redirects into the role-aware dashboard page */}
        <Route
          path="/view-applications"
          element={<RedirectToViewApplications />}
        />

        {/* 🔁 /bookmarks now redirects into the role-aware dashboard page */}
        <Route path="/bookmarks" element={<RedirectToBookmarks />} />

        {/* 🔁 /offer-to-sign now redirects into the role-aware dashboard page */}
        <Route
          path="/offer-to-sign/:playerId"
          element={gate(<RedirectToOfferToSign />)}
        />

        {/* /agent-verification now uses RoleVerification above */}
        <Route
          path="/agent-notifications"
          element={gate(<RedirectToNotifications />)}
        />

        {/* ⬇️ These now use shared RoleDashboard RoleExtras pages */}
        <Route
          path="/agent-contract-offers"
          element={gate(<ContractOffer />)}
        />
        <Route
          path="/agent-show-interest"
          element={gate(<ShowInterest />)}
        />
        <Route
          path="/agent-manage-contracts"
          element={gate(<ManageContract />)}
        />

        <Route path="/inbox" element={gate(<Inbox />)} />

        {/* Unprotected agent extras (kept as-is) */}
        <Route
          path="/agent/chats"
          element={gate(<RedirectToRoleChats />)}
        />



        <Route
          path="/agent/report"
          element={gate(
            <RequireProfile>
              <AgentReport />
            </RequireProfile>
          )}
        />

        <Route
          path="/agent/vsnworld"
          element={gate(
            <RequireProfile>
              <AgentVSNWorld />
            </RequireProfile>
          )}
        />


        {/* =========================
           PLAYER EXTRAS
           ========================= */}
        <Route
          path="/player-notifications"
          element={gate(
            <PlayerNotifications playerId={Number(localStorage.getItem("playerId"))} />
          )}
        />



        <Route path="/signing-offer" element={gate(<SigningOffer />)} />
        <Route path="/signing-interest" element={gate(<SigningInterest />)} />
        <Route path="/contracts" element={gate(<Contracts />)} />
        <Route
          path="/player-dashboard/training-videos"
          element={gate(<TrainingVideos />)}
        />
        <Route
          path="/player-dashboard/my-journey"
          element={gate(<MyJourney />)}
        />
        <Route
          path="/player-dashboard/player-reporting"
          element={gate(<PlayerReporting />)}
        />

        {/* Player inbox-style extras */}
        <Route path="/player/chats" element={gate(<PlayerChats />)} />
        <Route path="/player/comms" element={gate(<PlayerComms />)} />
        <Route path="/player/extras" element={gate(<PlayerExtras />)} />

        {/* =========================
           ADMIN EXTRAS
           ========================= */}
        <Route
          path="/admin/verifications"
          element={gate(<AdminRoleVerification />)}
        />
        {/* ✅ New unified admin contract review route */}
        <Route
          path="/admin/contracts"
          element={gate(<AdminContractReview />)}
        />



        <Route
          path="/admin/contract-monitor"
          element={gate(<ContractMonitor />)}
        />


        <Route
          path="/admin/revenue-sharing"
          element={gate(<RevenueSharing />)}
        />



        {/* ✅ New contract termination route */}
        <Route
          path="/admin/end-contracts"
          element={gate(<AdminContractTermination />)}
        />
        {/* ✅ New final player contract reports route */}
        <Route
          path="/admin/player-contract-reports"
          element={gate(<AdminFinalPlayerReport />)}
        />

        <Route
          path="/admin/player-career-reports"
          element={gate(<AdminPlayerCareerReview />)}
        />


        <Route
          path="/admin/transfermarkt-applications"
          element={gate(<AdminVSNIndexReview />)}
        />



        {/* Old /player-contract-review route -> redirect to new admin route */}
        <Route
          path="/player-contract-review"
          element={<Navigate to="/admin/player-contract-reports" replace />}
        />
        <Route
          path="/admin/unified-reports"
          element={gate(<AdminRoleReportReview />)}
        />

        <Route
          path="/admin/underage-messaging-requests"
          element={gate(<AdminUnderageMessagingReview />)}
        />


        <Route
          path="/review-agent-reports"
          element={<Navigate to="/admin/unified-reports" replace />}
        />

        <Route
          path="/refresh-youtube-videos"
          element={<RefreshYoutubeVideos />}
        />

        <Route
          path="/admin/leaderboard"
          element={gate(<AdminLeaderboard />)}
        />

        <Route
          path="/admin/approve-beta-access"
          element={gate(<ApproveBetaAccess />)}
        />

        <Route
          path="/admin/message-users"
          element={gate(<MessageUsers />)}
        />


        <Route
          path="/admin/manage-user-status"
          element={gate(<ManageUserStatus />)}
        />


        <Route
          path="/admin/limit-discoverability"
          element={gate(<LimitDiscoverability />)}
        />


        <Route
          path="/admin/user-lookup"
          element={gate(<UserLookup />)}
        />



        <Route
          path="/admin/video-review"
          element={gate(<VideoReview />)}
        />


        <Route
          path="/admin/maintenance"
          element={gate(<AdminMaintenancePage />)}
        />






        {/* Video Profile */}
        <Route
          path="/player-video-profile/:playerId"
          element={gate(<PlayerVideoProfile />)}
        />

        {/* 404 fallback */}
        <Route path="*" element={<div>404 Page Not Found</div>} />
      </Routes>
    </div>
  );
}

export default App;


























































